import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { products } from "../data/products";
import { readBasket, writeBasket, type BasketLine } from "./storage";

export type BasketItem = BasketLine & {
  name: string;
  slug: string;
  priceGbp: number;
};

type BasketContextValue = {
  items: BasketItem[];
  totalQuantity: number;
  subtotalGbp: number;
  add: (productId: string) => void;
  setQuantity: (productId: string, quantity: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
};

const BasketContext = createContext<BasketContextValue | null>(null);

export function BasketProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<BasketLine[]>(readBasket);

  useEffect(() => {
    writeBasket(lines);
  }, [lines]);

  const value = useMemo<BasketContextValue>(() => {
    const items = lines
      .map((line) => {
        const product = products.find((entry) => entry.id === line.productId);
        if (!product) {
          return null;
        }

        return {
          ...line,
          name: product.name,
          slug: product.slug,
          priceGbp: product.priceGbp
        };
      })
      .filter((item): item is BasketItem => item !== null);

    return {
      items,
      totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
      subtotalGbp: items.reduce(
        (sum, item) => sum + item.priceGbp * item.quantity,
        0
      ),
      add(productId) {
        setLines((current) => {
          const existing = current.find((line) => line.productId === productId);
          if (existing) {
            return current.map((line) =>
              line.productId === productId
                ? { ...line, quantity: line.quantity + 1 }
                : line
            );
          }
          return [...current, { productId, quantity: 1 }];
        });
      },
      setQuantity(productId, quantity) {
        setLines((current) => {
          if (quantity < 1) {
            return current.filter((line) => line.productId !== productId);
          }
          return current.map((line) =>
            line.productId === productId ? { ...line, quantity } : line
          );
        });
      },
      remove(productId) {
        setLines((current) =>
          current.filter((line) => line.productId !== productId)
        );
      },
      clear() {
        setLines([]);
      }
    };
  }, [lines]);

  return (
    <BasketContext.Provider value={value}>{children}</BasketContext.Provider>
  );
}

export function useBasket(): BasketContextValue {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error("useBasket must be used inside BasketProvider");
  }
  return context;
}
